/**
 * The next step after a result (ADR 0043, P5-LOOP-01): every play leads to the next one worth doing,
 * the first in the list being the main button. After a win, the next mission; after a loss, the
 * same again with a tip; today's daily if it isn't played yet; and when a game's line is done,
 * another game. Nothing starts by itself: these are buttons.
 */
import type { MissionInfo, SoloMission } from "./progress.js";
import { nextMission } from "./progress.js";

export type Step =
  | { kind: "next"; missionId: string; name: string }
  | { kind: "again"; missionId: string }
  | { kind: "daily"; missionId: string }
  | { kind: "game"; gameId: string; name: string };

export interface StepInput {
  result: "win" | "loss";
  missionId: string;
  list: readonly SoloMission[];
  /** Today's daily for this game and whether it's been played today; null without one. */
  daily: { missionId: string; played: boolean } | null;
  /** This play was today's daily. */
  wasDaily: boolean;
  /** Another game to suggest when this one's line is done, if there is one. */
  otherGame: { id: string; name: string } | null;
}

/** The steps to offer, the main one first. */
export function nextSteps(input: StepInput): Step[] {
  const { result, missionId, list, daily, wasDaily, otherGame } = input;
  const next = result === "win" ? nextMission(list, missionId) : undefined;
  const again: Step = { kind: "again", missionId };
  const dailyStep: Step[] =
    daily && !daily.played && !wasDaily ? [{ kind: "daily", missionId: daily.missionId }] : [];
  const game: Step[] = otherGame
    ? [{ kind: "game", gameId: otherGame.id, name: otherGame.name }]
    : [];
  if (result === "loss") return [again, ...dailyStep];
  if (next) return [{ kind: "next", missionId: next.id, name: next.name }, again, ...dailyStep];
  // The line (or track) is done: the daily if it's waiting, then another game.
  return [...dailyStep, ...game, again];
}

/** How far this mission's line has got: its name, missions won and how many there are. */
export function lineProgress(
  list: readonly SoloMission[],
  missionId: string,
): { name: string; won: number; total: number } | null {
  const m = list.find((x) => x.id === missionId);
  if (!m || m.pack || m.opensAfter || m.openFromStart) return null;
  const line = list.filter((x) =>
    m.track !== undefined
      ? x.track === m.track
      : x.track === undefined && !x.pack && !x.opensAfter && !x.openFromStart,
  );
  if (line.length < 2) return null;
  return {
    name: m.track ?? "Missions",
    won: line.filter((x) => x.stars !== undefined).length,
    total: line.length,
  };
}

/** A tip for after a loss: the game's tips in turn, by how many tries this visit. */
export const tipFor = (tips: readonly string[], run: number): string | null =>
  tips.length === 0 ? null : (tips[run % tips.length] ?? null);

/**
 * Another game to suggest: the one this person has won least in (solo progress), after this one
 * in the catalogue's order when it's a tie, so the suggestions go round.
 */
export function otherGameFor(
  gameId: string,
  games: readonly { id: string; name: string }[],
  wonIn: (id: string) => number,
): { id: string; name: string } | null {
  const at = games.findIndex((g) => g.id === gameId);
  const order = [...games.slice(at + 1), ...games.slice(0, Math.max(0, at))].filter(
    (g) => g.id !== gameId,
  );
  let best: { id: string; name: string } | null = null;
  let fewest = Infinity;
  for (const g of order) {
    const n = wonIn(g.id);
    if (n < fewest) {
      fewest = n;
      best = g;
    }
  }
  return best;
}

/**
 * What a win of this mission opens (ADR 0043, P5-LOOP-01's "bar to the next unlock"): the next in
 * its line, or in its track (ADR 0027), and the extras that open after it (ADR 0019). Never pack
 * missions: those open with the Pass.
 */
export function opensOnWin(missions: readonly MissionInfo[], id: string): MissionInfo[] {
  const at = missions.findIndex((m) => m.id === id);
  const m = missions[at];
  if (!m || m.pack) return [];
  const inLine = (x: MissionInfo) =>
    !x.pack && !x.opensAfter && !x.openFromStart && x.track === undefined;
  const after = missions.slice(at + 1);
  const next =
    m.track !== undefined
      ? after.find((x) => x.track === m.track && !x.pack)
      : inLine(m)
        ? after.find(inLine)
        : undefined;
  const extras = missions.filter((x) => x.opensAfter === id && !x.pack);
  return [...(next ? [next] : []), ...extras];
}

/**
 * The line under a result about what's next to open: after a loss, what a win would open; after a
 * first win, what it opened. `isOpen` says what's open now (after the result); `wasOpen`, before.
 */
export function unlockLine(
  missions: readonly MissionInfo[],
  id: string,
  result: "win" | "loss",
  isOpen: (id: string) => boolean,
  wasOpen: (id: string) => boolean = isOpen,
): string | null {
  const targets = opensOnWin(missions, id);
  if (result === "loss") {
    const locked = targets.filter((m) => !isOpen(m.id));
    return locked.length > 0 ? `Win it to open ${names(locked)}.` : null;
  }
  const opened = targets.filter((m) => isOpen(m.id) && !wasOpen(m.id));
  return opened.length > 0 ? `You opened ${names(opened)}.` : null;
}

const names = (ms: readonly MissionInfo[]) => {
  const all = ms.map((m) => m.name);
  return all.length <= 1
    ? (all[0] ?? "")
    : `${all.slice(0, -1).join(", ")} and ${all.at(-1) ?? ""}`;
};
